import React from 'react';

/**
 * Timeline component for the deployment process
 * Displays all stages and user progress
 * @param {Object} props - Component props
 * @param {Array} props.stages - Array of all deployment stages
 * @param {Array} props.infoCards - Array of all information cards
 * @param {string} props.currentStage - ID of the current active stage
 * @param {Object} props.deployStatus - Current deployment status data
 */
const AppStatusTimeline = ({ stages, infoCards, currentStage, deployStatus }) => {
  // Combine stages and info cards for timeline display
  const allTimelineItems = [];
  
  // Add all stages and info cards to a single array
  if (stages && stages.length > 0) {
    stages.forEach(stage => {
      allTimelineItems.push({
        ...stage,
        type: 'stage',
        _displayOrder: stage.order || 0 // Use 0 as default if order is undefined
      });
    });
  }
  
  if (infoCards && infoCards.length > 0) {
    infoCards.forEach(card => {
      allTimelineItems.push({
        ...card,
        type: 'information',
        _displayOrder: card.order || 0 // Use 0 as default if order is undefined
      });
    });
  }
  
  // Sort the items by their order property
  allTimelineItems.sort((a, b) => a._displayOrder - b._displayOrder);

  return (
    <div className="fixed left-8 top-1/2 -translate-y-1/2 z-30">
      {/* Add title above the timeline */}
      <h2 className="text-xl font-bold text-white mb-6">App Upload Status</h2>
      
      <div className="flex flex-col space-y-6 relative">
        {/* Vertical connecting line */}
        <div className="absolute left-4 top-4 bottom-4 w-0.5 bg-gray-700"></div>
        
        {/* Start Point - Updated to match information card design */}
        <div className="flex items-center relative z-10">
          <div className={`w-8 h-8 rounded-full transition-all duration-500 flex items-center justify-center
            ${deployStatus?.status === 'in-progress' || deployStatus?.status === 'completed' 
              ? 'bg-blue-500 text-white' 
              : 'bg-blue-900/50 text-blue-300'
            }`}
          >
            {/* Start icon */}
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
          </div>
          <span className={`ml-4 text-sm font-medium ${
            deployStatus?.status === 'in-progress' || deployStatus?.status === 'completed'
              ? 'text-blue-500'
              : 'text-blue-300'
          }`}>Start</span>
        </div>
        
        {/* All Timeline Items (Stages and Information Cards) */}
        {allTimelineItems.map((item, index) => (
          <div key={item.id} className="relative z-10">
            <div className="flex items-center">
              {/* Different styling for stages vs information cards */}
              {item.type === 'stage' ? (
                /* Stage number circle */
                <div className={`w-8 h-8 rounded-full transition-all duration-500 flex items-center justify-center
                  ${item.id === currentStage
                    ? 'bg-primary text-black'
                    : deployStatus?.stages?.[item.id]?.status === 'completed'
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-700 text-gray-400'
                  }`}
                >
                  {/* Show number for stages */}
                  {stages.findIndex(stage => stage.id === item.id) + 1}
                </div>
              ) : (
                /* Information card icon */
                <div className={`w-8 h-8 rounded-full transition-all duration-500 flex items-center justify-center
                  ${deployStatus?.informationViewed?.[item.id]
                    ? 'bg-blue-500 text-white'
                    : 'bg-blue-900/50 text-blue-300'
                  }`}
                >
                  {/* Info icon for information cards */}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              )}
              
              {/* Item title */}
              <span className={`ml-4 text-sm font-medium ${
                item.type === 'stage' ? (
                  item.id === currentStage
                    ? 'text-primary'
                    : deployStatus?.stages?.[item.id]?.status === 'completed'
                    ? 'text-green-500'
                    : 'text-gray-400'
                ) : (
                  deployStatus?.informationViewed?.[item.id]
                    ? 'text-blue-500'
                    : 'text-blue-300'
                )
              }`}>
                {item.title}
              </span>
            </div>

            {/* Substeps (only displayed for closedTesting stage when active) */}
            {item.type === 'stage' && item.id === 'closedTesting' && currentStage === 'closedTesting' && item.substeps && (
              <div className="ml-12 mt-2 space-y-1.5">
                {item.substeps.map((substep) => {
                  const isCompleted = !!deployStatus?.stages?.closedTesting?.answers?.[substep.id];
                  const isActive = item.id === currentStage;
                  return (
                    <div key={substep.id} className="flex items-center gap-2">
                      {/* Substep status indicator */}
                      <div className={`w-4 h-4 rounded-full flex items-center justify-center text-xs
                        ${isCompleted 
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-700 text-gray-400'
                        }`}
                      >
                        {/* Icon for substep status */}
                        {isCompleted ? (
                          <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <div className="w-1.5 h-1.5 bg-gray-500 rounded-full"></div>
                        )}
                      </div>
                      {/* Substep title */}
                      <span className={`text-xs ${
                        isCompleted 
                          ? 'text-green-500'
                          : 'text-gray-400'
                      }`}>
                        {substep.title}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AppStatusTimeline; 